import nkf from './nkf/en';

export default {
  nkf,
  notifications: {
    actions: {
      ACKNOWLEDGED: 'I’ve read it',
      DISMISSED: 'Close without confirming',
      TAKEN: 'Taken',
      SKIPPED: 'Skip this dose',
      SNOOZED: 'Remind me later',
      FULFILLED: 'Complete',
      CANCELLED: 'Cancel',
    },
    outcomes: {
      ACKNOWLEDGED: 'Confirmed as read',
      DISMISSED: 'Closed without confirmation',
      TAKEN: 'Recorded as taken',
      SKIPPED: 'Dose recorded as skipped',
      SNOOZED: 'Reminder postponed',
      FULFILLED: 'Check-in recorded',
      CANCELLED: 'Notification no longer active',
      readingSaved: 'Reading recorded',
      dryWeightViewed: 'Dry-weight update viewed',
      moodSaved: 'Mood check-in recorded',
      alertClosed: 'Alert closed',
    },
    summary: 'Notification details',
    response: 'Saved response',
    lastResponse: 'Last recorded response:',
    recordedBy: 'Recorded by',
    nextReminder: 'Next reminder: {time}',
    notificationDate: 'Notification date',
    originalMessage: 'Original message',
    dialysisDates: 'Dialysis days recorded',
    recordedNote: 'Note saved with this check-in',
    savingResponse: 'Saving your response…',
    readConfirmationQuestion: 'Have you read this update?',
    closedDescription: 'Closing a notification does not record a health check-in.',
    notDueDescription: 'You can respond when this reminder is due.',
    readOnlyDescription: 'You can still view this message, but can no longer respond to it.',
    answers: {
      attended: 'Attended dialysis',
      notAttended: 'Did not attend dialysis',
    },
    actors: {
      PATIENT: 'Patient',
      CAREGIVER: 'Caregiver',
      STAFF: 'Care team',
      SYSTEM: 'Automatic update',
    },
    states: {
      pending: 'Not due yet',
      completed: 'This notification has already been answered',
      cancelled: 'This notification is no longer active',
      expired: 'This reminder has ended',
      snoozed: 'Reminder postponed',
    },
    unread: 'Unread',
  },

  shared: {
    validation: {
      type_string: 'This field must be a string',
      type_number: 'This field must be a number',
      type_integer: 'This field must be an integer',
      type_boolean: 'This field must be a boolean',
      type_array: 'This field must be an array',
      type_object: 'This field must be an object',
      type_null: 'This field must be empty',
      required: 'This field is required',
      min_length: 'This field must be at least {minLength} characters',
      max_length: 'This field must contain no more than {maxLength} characters',
      pattern: 'This field format is invalid',
      format: 'This field format is invalid',
      min: 'Value must be at least {min}',
      max: 'Value must be at most {max}',
      min_items: 'Array must contain at least {minItems} item(s)',
      max_items: 'Array must contain at most {maxItems} item(s)',
      unique_items: 'Array items must be unique',
      additional_properties: 'Additional properties are not allowed',
      enum: 'Value must be one of: {allowedValues}',
      conditional: 'This field combination is invalid',
      not_allowed: 'This field combination is not allowed',
      invalid: 'This value is invalid',
      exclusive_min: 'This field must be greater than {min}',
      exclusive_max: 'This field must be less than {max}',
      multiple_of: 'This field must be a multiple of {multipleOf}',
      weight_decimal_places: 'Enter a weight with no more than 2 decimal places.',
      min_properties: 'Provide at least {limit} fields',
      max_properties: 'Provide no more than {limit} fields',
      const: 'This value does not match the required value',
      property_names: 'A field name is invalid',
      dependencies: 'Complete the related required fields',
      contains: 'Include an item matching the required conditions',
      format_email: 'Please enter a valid email address',
      format_uri: 'Please enter a valid URI',
      format_date: 'Use the date format YYYY-MM-DD',
      format_time: 'Please enter a valid time',
      clock_time: 'Use the 24-hour time format HH:mm',
      local_datetime: 'Enter a valid date and time',
    },
  },

  common: {
    cancel: 'Cancel',
    saving: 'Saving...',
    loading: 'Loading...',
    loadMore: 'Load more',
    startupFailed: 'The app could not start. Please try again.',
    retry: 'Try again',
    error: {
      connAborted: 'This is taking longer than expected. Please try again.',
      connRefused: 'We couldn’t connect to the service. Please try again shortly.',
      connReset: 'We couldn’t connect to the service. Please try again shortly.',
      timedOut: 'This is taking longer than expected. Please try again.',
      hostUnreachable: 'We couldn’t connect to the service. Please try again shortly.',
      notFound: 'We couldn’t connect to the service. Please try again shortly.',
      unexpected: 'We couldn’t complete this action. Please try again.',
      unexpectedConnection:
        'We couldn’t connect. Please check your internet connection and try again.',
    },
  },

  toast: {
    pushRegistrationFailed: 'Failed to enable push notifications. Please try again.',
    restClient: {
      sessionExpired: 'Your session has expired. Please log in again.',
      httpUnauthorized: 'Please sign in again to continue.',
      httpForbidden: 'You don’t have permission to do this. Please contact your care team.',
      httpNotFound: 'We couldn’t find this information. Please try again.',
      httpInternalServerError: 'We couldn’t complete this action. Please try again shortly.',
      httpGeneric: 'We couldn’t complete this action. Please try again.',
      networkTimeout: 'This is taking longer than expected. Please try again.',
      networkError: 'We couldn’t connect. Please check your internet connection and try again.',
    },
  },
  auth: {
    setupNoticeTitle: 'Create your own password',
    continue: 'Continue',
    setupNotice:
      'You signed in with a password provided by your administrator. For your security, please set a new password before continuing.',
    expiredTitle: 'Your password has expired',
    expiredNotice: 'Passwords must be changed every 12 months. Yours was last changed on {date}.',
    setNewPassword: 'Set new password',
    setupTitle: 'Create a new password',
    expiredFormTitle: 'Change your password',
    currentPasswordPlaceholder: 'Enter current password',
    newPasswordPlaceholder: 'Create a new password',
    confirmPasswordPlaceholder: 'Re-enter new password',
    setupDescription:
      'Choose a password that only you know. You will use it to log in from now on.',
    expiredDescription:
      'Your password is older than 12 months. Enter your current password, then choose a new one.',
    changeDescription: 'Enter your current password, then choose a new one.',
    setupNote: 'You are signed in with a temporary password set by your administrator.',
    updatedTitle: 'Password updated',
    setupSuccess: 'Your new password is set. Let’s continue.',
    changeSuccess: 'Your password has been changed. Use it the next time you log in.',
    saveNewPassword: 'Save new password',
    updatePassword: 'Update password',
    passwordsMatch: 'Passwords match',
    strengthWeak: 'Weak',
    strengthFair: 'Fair',
    strengthGood: 'Good',
    strengthStrong: 'Strong',
    showPassword: 'Show password',
    hidePassword: 'Hide password',

    brandSubtitle: 'Dialysis',
    welcome: 'Welcome back',
    loginDescription: 'Log in to manage your dialysis care and daily health information.',
    loginHelp: 'Need help logging in?',
    privacyNotice: 'Protected health information. Only for registered dialysis patients.',
    username: 'Username',
    usernameRequirements: 'Enter the username provided by your care team.',
    password: 'Password',
    enterUsername: 'Enter your username.',
    enterPassword: 'Enter your password.',
    contactCareTeam: 'Contact your care team to reset the password.',
    signIn: 'Log in',
    currentPassword: 'Current password',
    enterCurrentPassword: 'Enter your current password.',
    newPassword: 'New password',
    enterNewPassword: 'Enter your new password.',
    confirmNewPassword: 'Confirm new password',
    passwordLength: '8–64 characters.',
    passwordMaximumLength: 'Password must not exceed 64 characters.',
    passwordCase: 'Upper and lower case letters',
    passwordNumber: 'At least one number',
    passwordSymbol: "At least one symbol, e.g. ! {'@'} # $",
    noActiveTreatmentTitle: 'No active treatment found',
    noActiveTreatmentDescription:
      'There is currently no active treatment linked to your care record. Please contact your care team for assistance.',
    passwordRequirements: 'Your password does not meet the requirements.',
    passwordDifferent: 'Your new password must be different from the current one.',
    passwordMismatch: 'The new passwords do not match.',
    retryAfter: 'Try again in {seconds}s',
    challengeExpired: 'This request has expired. Please sign in again.',
    savedSessionCleanupFailed: 'Your session ended, but saved sign-in data could not be cleared.',
    passwordChangedSignIn: 'Password updated. Sign in with your new password.',
    changePassword: 'Change password',
    accessUnavailable: 'Access unavailable',
    returnToSignIn: 'Return to sign in',
  },

  appStatus: {
    checkingSessionTitle: 'Checking your session',
    checkingTitle: 'Checking for updates',
    checkingMessage: 'This will only take a moment.',
    checkFailedTitle: 'Unable to continue',
    checkFailedMessage:
      'Please check your internet connection and try again. If your connection is working, the service may be temporarily unavailable.',
    offlineTitle: 'No internet connection',
    offlineMessage: 'Connect to Wi-Fi or mobile data, then try again.',
    retry: 'Try again',
    updateTitle: 'App update required',
    updateMessage: 'Please update the app to continue.',
    update: 'Update now',
  },

  languages: {
    en: 'English',
    zh: 'Chinese',
  },
};
